import { OASElement } from '@oas-ui/core'
// 图标走 @oas-ui/icons 运行时查表（registerIcon 自定义注册优先、内置集兜底，与 oas-icon 同源）——
// 不经 oas-icon 组件模块，避免把整个 <oas-icon> 拉进 menu 按需链
import { lookupIcon } from '@oas-ui/icons/runtime'
import { TOUCH_TARGET_CSS } from '../../shared/touch-target.js'
import { isRtl } from '../../shared/direction.js'
// 数据载体类：前置媒体（slot="leading"）的提取/判定工具挂在其类上（插槽标记归属载体，api:scan 归属正确）
import { OASMenuItem } from './oas-menu-item.js'

export type MenuItemType = 'item' | 'group' | 'divider'

/** 叶子项语义类型：radio（默认，选中态打勾，参与 value）/ action（动作，无勾选态，不写回 value）/ checkbox（多选勾选，value 为数组勾选集） */
export type MenuItemKind = 'radio' | 'action' | 'checkbox'

export interface MenuItem {
  label?: string
  value?: string
  disabled?: boolean
  /** 加载中：渲染 spinner、禁点（点击/键盘/hover 均拦截），由数据驱动恢复 */
  loading?: boolean
  icon?: string
  /** 图标颜色：显式固定该色（优先于选中/禁用态默认色）；缺省 currentColor 随文字色 */
  iconColor?: string
  /** 菜单项类型：普通项（默认）/ 分组 / 分隔线 */
  type?: MenuItemType
  /** 叶子项语义：radio（默认，可勾选）/ action（动作项，无勾选态、不写回 value）/ checkbox（多选勾选，value 数组勾选集） */
  kind?: MenuItemKind
  /** 破坏性项：红色语义（删除/退出等危险操作） */
  danger?: boolean
  /** 链接项：渲染 <a>（锚点语义，选中即跳转），搭配 target/rel */
  href?: string
  target?: string
  rel?: string
  /** 子菜单项，支持多级嵌套（任意层级）；group 的 children 平铺展示在同一层 */
  children?: MenuItem[]
  /** 快捷键标注（如 Ctrl+N）：渲染为右端 kbd 提示，与 menubar 视觉契约一致 */
  shortcut?: string
  /**
   * 前置媒体（子元素声明式通道 slot="leading"）：头像/色块等任意前置内容（克隆节点），
   * 渲染在 label 前、优先于 `icon`（同时给时 icon 不渲染）；items JSON 通道不支持。
   */
  leading?: Node[]
}

/** 过滤视图去重相邻 / 首尾分隔线（过滤后无项的区间不留悬空分隔线） */
function trimDividers(list: MenuItem[]): MenuItem[] {
  const out: MenuItem[] = []
  for (const item of list) {
    if (item.type === 'divider') {
      if (out.length === 0 || out[out.length - 1]!.type === 'divider') continue
      out.push(item)
    } else {
      out.push(item)
    }
  }
  while (out.length > 0 && out[out.length - 1]!.type === 'divider') out.pop()
  return out
}

const STYLE = `
:host {
  display: block;
  font-family: inherit;
  background: var(--oas-color-bg);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  padding: var(--oas-space-1);
  min-width: 160px;
  color: var(--oas-color-text-primary);
}
:host([hidden]) {
  display: none;
}
.menu {
  margin: 0;
  padding: 0;
  list-style: none;
}
.item {
  position: relative;
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  font-size: var(--oas-font-size-md);
  display: flex;
  align-items: center;
  white-space: nowrap; /* 禁止中文菜单项逐字换行竖排 */
}
/* href 链接项渲染 <a>：必须显式 color:inherit 压过 UA 链接色（Chrome dark UA 的
   未访问/已访问链接默认色对深底不达标，继承色打不过 UA 指定色）；选中/danger 等
   类规则特异性更高，仍正常覆盖 */
a.item,
a.item:visited {
  color: inherit;
  text-decoration: none;
}
/* 标签占据中间剩余空间并起始侧对齐，贴住前导图标；尾侧 arrow/check 靠尾 */
.item .label {
  flex: 1;
  min-width: 0;
  text-align: start;
}
.item:hover,
.item.active {
  background: var(--oas-color-bg-hover);
}
.item[aria-checked='true'] {
  color: var(--oas-color-primary);
  font-weight: 500;
}
.item[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}
/* loading 项：spinner + 弱化文字，光标 wait；点击/键盘由 JS 拦截（aria-disabled 语义同步） */
.item.loading {
  cursor: wait;
  opacity: 0.7;
}
.item.loading .label {
  color: var(--oas-color-text-secondary);
}
/* spinner：CSS 圆环旋转，占用图标位，跟随 secondary 色（light/dark token 自动适配） */
.spin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.9em;
  height: 0.9em;
  margin-inline-end: var(--oas-space-2);
  flex-shrink: 0;
  border: 2px solid var(--oas-color-text-secondary);
  border-top-color: transparent;
  border-radius: 50%;
  animation: oas-spin 0.8s linear infinite;
}
@keyframes oas-spin {
  to {
    transform: rotate(360deg);
  }
}
.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  height: 1em;
  margin-inline-end: var(--oas-space-2);
  flex-shrink: 0;
  color: inherit;
}
.icon svg {
  display: block;
  width: 1em;
  height: 1em;
}
/* 前置媒体位（子元素声明式通道 slot="leading"）：头像/色块等任意前置内容，
   与 .icon 同档间距，尺寸跟随内容（avatar/自定义节点自带尺寸） */
.media {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-inline-end: var(--oas-space-2);
  flex: none;
}
.check {
  opacity: 0;
}
.item[aria-checked='true'] .check {
  opacity: 1;
}
/* checkbox 勾选框：与 radio 的 ✓ 区分——方块边框（未勾空框、勾选主色填充+✓） */
.check--box {
  width: 14px;
  height: 14px;
  border: 1px solid var(--oas-color-border-strong);
  border-radius: var(--oas-radius-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-inline-end: var(--oas-space-2); /* 与 .icon/.spin 同档间距，避免方块紧贴标签 */
  opacity: 1; /* checkbox 的框始终显示（区别于 radio 的 ✓ 仅勾选显示） */
}
.item[role='menuitemcheckbox'][aria-checked='true'] .check--box {
  background: var(--oas-color-primary);
  border-color: var(--oas-color-primary);
}
.item[role='menuitemcheckbox'][aria-checked='true'] .check--box::after {
  content: '✓';
  color: var(--oas-color-text-on-primary);
  font-size: var(--oas-font-size-xs);
  line-height: 1;
}
/* danger 破坏性项：红色语义（文字+图标同色系，hover/active 加深） */
.item.danger {
  color: var(--oas-color-danger);
}
.item.danger:hover,
.item.danger.active {
  background: color-mix(in srgb, var(--oas-color-danger) 12%, transparent);
  color: var(--oas-color-danger);
}
/* max-height：长菜单内部滚动 */
:host([max-height]) .menu {
  overflow-y: auto;
  max-height: var(--oas-menu-max-height, none);
}
/* ===== searchable：顶部过滤输入框 ===== */
.menu-search {
  padding: var(--oas-space-1) var(--oas-space-1) var(--oas-space-2);
  border-bottom: 1px solid var(--oas-color-border);
  margin-bottom: var(--oas-space-1);
}
.menu-search[hidden] {
  display: none;
}
.menu-search-input {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-size: var(--oas-font-size-md);
  color: var(--oas-color-text-primary);
  background: var(--oas-color-bg);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-sm);
  padding: var(--oas-space-1) var(--oas-space-2);
}
.menu-search-input::placeholder {
  color: var(--oas-color-text-secondary);
}
.menu-search-input:focus-visible {
  outline: 2px solid var(--oas-color-primary);
  outline-offset: 1px;
  border-color: var(--oas-color-primary);
}
/* 过滤无匹配空态（复用 i18n select.noMatch） */
.menu-search-empty {
  padding: var(--oas-space-3);
  text-align: center;
  color: var(--oas-color-text-secondary);
  font-size: var(--oas-font-size-sm);
  white-space: nowrap;
}
.menu-search-empty[hidden] {
  display: none;
}
/* shortcut kbd：右端快捷键标注（对齐 menubar shortcut 视觉契约；颜色走 token） */
.shortcut {
  margin-inline-start: var(--oas-space-3);
  padding: 0 var(--oas-space-1);
  font-size: var(--oas-font-size-sm);
  font-family: var(--oas-font-family-mono, monospace);
  color: var(--oas-color-text-primary);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-xs);
  background: var(--oas-color-bg-hover);
  line-height: 1.6;
  flex-shrink: 0;
}
/* ===== inline 导航形态：子菜单就地展开（非浮出） ===== */
/* inline 展开容器：默认收起（grid-template-rows 0fr 过渡），open 展开（1fr） */
.inline-sub {
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows var(--oas-transition-base) var(--oas-ease-out);
  /* 父项 li 是 flex 行（icon/label/arrow），inline-sub 作为其子元素须占满整行并换行到下方，
     否则会成为 flex 子项与 arrow 并排（子项跑到父项右侧） */
  flex: 1 1 100%;
  width: 100%;
}
.inline-sub > ul {
  min-height: 0;
  overflow: hidden;
  list-style: none;
  margin: 0;
  padding: 0;
}
.inline-sub.open {
  grid-template-rows: 1fr;
}
/* inline 父项允许换行：inline-sub（flex: 1 1 100%）换到父项内容下方 */
:host(.oas-menu--inline) .item {
  flex-wrap: wrap;
}
/* inline 子项缩进层级视觉 */
.inline-sub .item {
  padding-inline-start: calc(var(--oas-space-4) + var(--oas-space-2));
}
/* inline 模式展开箭头：chevron-down 指示（就地展开方向） */
:host(.oas-menu--inline) .item[aria-haspopup] .arrow {
  transition: transform var(--oas-transition-base) var(--oas-ease-out);
}
:host(.oas-menu--inline) .item.open .arrow {
  transform: rotate(90deg);
}
/* RTL：级联右向 chevron 镜像为左向（子菜单开在父项左侧）；inline 的 down 箭头水平对称不受影响 */
:host([data-rtl]) .item[aria-haspopup] > .arrow svg {
  transform: scaleX(-1);
}
/* RTL：inline 展开旋转反向（开态指向行内轴另一侧） */
:host([data-rtl].oas-menu--inline) .item.open .arrow {
  transform: rotate(-90deg);
}
/* ===== 水平溢出收纳：超宽项收进「···」收纳子菜单 ===== */
/* 被收项在主流隐藏（data-collapsed），收纳子菜单里镜像显示 */
:host([mode='horizontal']) .item[data-collapsed] {
  display: none;
}
.menu-more {
  flex-shrink: 0;
}
.menu-more[hidden] {
  display: none;
}
/* 选中项被收纳时「···」高亮：与条上选中项（aria-checked=true）同风格 */
.menu-more.child-selected {
  color: var(--oas-color-primary);
  font-weight: 500;
}
/* 「···」弹层：右对齐其右缘（它在条末尾，向左开会超出容器右缘被 overflow-x:clip 裁掉）；
   且重置 color/font-weight——child-selected 的高亮色只作用于「···」本身，不继承进弹层镜像项。
   特异性须压过 :host([mode='horizontal']) .submenu-1（同为 (0,3,0)，源码序它更靠后会赢），
   用 .menu-more > .menu-more-sub 加子代组合升权。
   注意 color 必须用 --oas-color-text-primary（组件已有 token），
   勿用 --oas-color-text（无此 token，var 未定义会回退成继承色） */
:host([mode='horizontal']) .menu-more > .menu-more-sub {
  left: auto;
  right: 0;
  color: var(--oas-color-text-primary);
  font-weight: normal;
}
.arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-inline-start: var(--oas-space-3);
  color: var(--oas-color-text-secondary);
  font-size: var(--oas-font-size-sm);
  flex-shrink: 0;
}
.arrow svg {
  display: block;
  width: 1em;
  height: 1em;
}
/* 分组标题：小字次要色，不可点 */
.group {
  list-style: none;
  margin-top: var(--oas-space-2);
  padding: var(--oas-space-1) var(--oas-space-3);
  cursor: default;
}
.group:first-child {
  margin-top: 0;
}
.group-label {
  display: block;
  font-size: var(--oas-font-size-sm);
  font-weight: 500;
  color: var(--oas-color-text-secondary);
  white-space: nowrap;
}
/* 分隔线：细分隔线，不可点 */
.divider {
  list-style: none;
  height: 1px;
  margin: var(--oas-space-1) 0;
  background: var(--oas-color-border);
  cursor: default;
}
/* 级联浮出子菜单：默认隐藏，父项 .open 时显示；逻辑定位在父项行内轴起始侧的对面
   （LTR 开在右侧、RTL 开在左侧，inset-inline-start 自动跟随书写方向） */
.submenu {
  display: none;
  list-style: none;
  margin: 0;
  padding: var(--oas-space-1);
  position: absolute;
  inset-inline-start: 100%;
  top: calc(-1 * var(--oas-space-1));
  min-width: 140px;
  background: var(--oas-color-bg);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  box-shadow: var(--oas-shadow-md);
  z-index: calc(var(--oas-z-index-base, 0) + 10);
}
.item.open > .submenu {
  display: block;
}
/* 视口边界翻转（JS 检测后切类）：行内轴前方空间不足时翻到另一侧展开、底部不足向上展开
   （flip-left 类名沿用历史语义 = 「子菜单翻到行内轴另一侧」，逻辑 inset 使其自动跟随方向） */
.submenu.flip-left {
  inset-inline-start: auto;
  inset-inline-end: 100%;
}
.submenu.flip-up {
  top: auto;
  bottom: calc(-1 * var(--oas-space-1));
}
:host([mode='horizontal']) .submenu-1.flip-up {
  top: auto;
  bottom: calc(100% + var(--oas-space-1));
  margin-top: 0;
}
/* 水平模式一级子菜单回折：.submenu-1 定位规则特异性 (0,3,0) 压过通用 .submenu.flip-left
   (0,2,0)——必须给同权规则，否则 flip-left 的逻辑 inset 被 inset-inline-start: 0 覆盖，
   类名切了面板不动（越过 .menu 右缘仍被 overflow-x: clip 裁掉）。逻辑 inset 使 RTL 自动镜像。
   :not(.menu-more-sub) 排除「···」收纳弹层——它固定右对齐其收纳项右缘（见上方规则），不参与回折。
   回折后仍越出裁切边界（面板宽于容器给任一侧的空间）时，由 syncSubmenuPositions 追加 inline
   translateX 最小平移夹回（见该方法注释）——不在此声明，避免与行内位移叠加。 */
:host([mode='horizontal']) .submenu-1.flip-left:not(.menu-more-sub) {
  inset-inline-start: auto;
  inset-inline-end: 100%;
}
/* 水平模式：顶部导航条样式，菜单项横排 */
:host([mode='horizontal']) {
  display: inline-block;
  min-width: 0;
  padding: 0;
  border-radius: var(--oas-radius-lg);
}
:host([mode='horizontal']) .menu {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: var(--oas-space-1);
  /* 水平收纳前提：容器宽度受 host 约束（host 设宽度时 .menu 不超宽），超出项收进「···」。
     用 overflow-x: clip 而非 overflow:hidden——hidden 会双轴裁剪，把向下浮出的一级子菜单
     （及「···」收纳弹层）一并裁掉（display/rect 正常但视觉不可见）；clip 只裁横轴、纵轴放行 */
  max-width: 100%;
  overflow-x: clip;
  overflow-y: visible;
}
/* 水平模式一级子菜单向下浮出；二级及以上仍向右。逻辑 inset：LTR 贴父项左缘、RTL 贴父项
   右缘（书写起点侧），与浮出子菜单的书写方向约定一致（物理 left:0 在 RTL 下不镜像） */
:host([mode='horizontal']) .submenu-1 {
  top: 100%;
  inset-inline-start: 0;
  margin-top: var(--oas-space-1);
}
/* 收起态（仅 vertical）：菜单收窄只显示图标 */
:host(:not([mode='horizontal'])[collapsed]) {
  min-width: 0;
}
:host(:not([mode='horizontal'])[collapsed]) .item {
  justify-content: center;
  padding: var(--oas-space-2);
}
:host(:not([mode='horizontal'])[collapsed]) > .menu > .item > .label,
:host(:not([mode='horizontal'])[collapsed]) > .menu > .item > .arrow,
:host(:not([mode='horizontal'])[collapsed]) > .menu > .item > .check,
:host(:not([mode='horizontal'])[collapsed]) > .menu > .item > .shortcut,
:host(:not([mode='horizontal'])[collapsed]) .group-label {
  display: none;
}
:host(:not([mode='horizontal'])[collapsed]) .item > .icon,
:host(:not([mode='horizontal'])[collapsed]) .item > .media {
  margin-inline-end: 0;
}
/* collapsed 收起态：媒体位与图标一致收敛——不写 display:none（隐藏会让条目只剩空 padding
   高度，成为可 hover/点击却无内容的空白块），仅靠上一条规则的 margin 归零收敛；
   头像/色块正是收起态 rail 的视觉锚点，保持可见 */
/* ===== disabled 整单禁用：视觉降饱和（ui-spec §2.3）+ 子项交互反馈抑制；
   点击/悬停/键盘由 JS 全拦截（href 链接项同时 preventDefault 阻断原生跳转）。
   置于 TOUCH_TARGET 之前声明序后段：与 .item.danger:hover 同特异性时后写胜出 ===== */
:host([disabled]) .item,
:host([disabled]) .item.danger {
  /* 整单禁用时 danger 红不再保留色相——降饱和后红色在感知对比度门禁下不达标（a11y 实抓）；
     统一走禁用文字色（与全库 disabled 观感一致），faded-disabled 豁免可识别 */
  color: var(--oas-color-text-disabled);
}
:host([disabled]) {
  /* 不再叠 opacity——禁用色 × 透明度双层淡化会把文字洗出门禁线（实测 1.68）；
     禁用观感由「文字走禁用色 + not-allowed 光标 + 无 hover」承载（与全库 disabled 一致且豁免可识别） */
  cursor: not-allowed;
}
:host([disabled]) .item {
  cursor: not-allowed;
}
:host([disabled]) .item:hover,
:host([disabled]) .item.active {
  background: none;
}
/* 触摸目标抬升：coarse pointer 下菜单项行最小高度 ≥44px（token 开口可调）。
   dropdown item / menu-item 由本组件统一渲染，随此处一并生效 */
${TOUCH_TARGET_CSS}
`

export class OASMenu extends OASElement {
  static override get observedAttributes(): string[] {
    return [
      'items',
      'value',
      'mode',
      'collapsed',
      'theme',
      'max-height',
      'expanded',
      'accordion',
      'close-on-select',
      'open-on-hover',
      // 整单禁用：交互全拦截 + 降饱和 + aria-disabled
      'disabled',
      // selectable=false：纯动作菜单（无选中态语义，不写回 value）
      'selectable',
      // 选中后不自动收起浮出子菜单（close-on-select 族正向开关）
      'persistent',
      // searchable：顶部过滤输入框，键入实时过滤可见项
      'searchable',
      'dir',
    ]
  }

  private itemsList: MenuItem[] = []
  /** 当前键盘导航所在层级的祖先 value 链（空数组 = 顶层） */
  private activeStack: string[] = []
  private activeIndex = -1
  /** searchable 过滤关键词（轻量非受控；关闭 searchable 时清空） */
  private searchTerm = ''
  /** 过滤态下需要自动展开的祖先 value 集合（命中项路径，不改动受控 expanded） */
  private searchExpand = new Set<string>()
  /** 已展开的子菜单 value 集合（单条展开路径） */
  private expanded = new Set<string>()
  private menuEl: HTMLElement | null = null
  /** typeahead 字符缓冲（连续输入定位匹配项） + 超时定时器 */
  private typeaheadBuffer = ''
  private typeaheadTimer: ReturnType<typeof setTimeout> | null = null
  /** 水平溢出收纳的 ResizeObserver（horizontal 模式容器宽度变化时重算收纳） */
  private overflowObserver: ResizeObserver | null = null
  /** 水平溢出收纳项「···」元素引用（渲染时捕获，syncOverflowCollapse 更新显隐与子菜单内容） */
  private moreItemEl: HTMLElement | null = null
  /** 子元素通道观察器：light DOM 里 oas-menu-item/group/divider 增删或属性/文本变化 → 重解析渲染 */
  private childObserver: MutationObserver | null = null
  /** 桥区观察器（仅「指针离开菜单盒但仍在父项 → 面板桥区内」时挂载，收起即卸载） */
  private bridgeWatch: { move: (e: MouseEvent) => void; leave: () => void } | null = null
  /** 桥区观察器最后已知指针位置（滚动 / 缩放后按它重判，见 recheckHoverBridge） */
  private bridgePointer: { x: number; y: number } | null = null
  /** open-on-hover 延迟开/关定时器（父项悬停 ~150ms 开 / ~300ms 关）；关闭计时按 value 归属 */
  private hoverOpenTimer: ReturnType<typeof setTimeout> | null = null
  private hoverCloseTimer: ReturnType<typeof setTimeout> | null = null
  private hoverCloseValue: string | null = null

  /**
   * 视口尺寸/滚动变化重定位（类字段引用：addEventListener 规范去重 + onReconnect 幂等重挂）。
   * 展开态下重算子菜单翻转；桥区保持期间滚动 / 缩放：指针没动但菜单盒与面板整体位移 →
   * 按最后已知指针位置重判，避免留下「指针早已不在路径上、面板还开着」的悬挂态
   * （滚动不保证补发 mousemove）。
   */
  private repositionOnViewportChange = (): void => {
    if (this.expanded.size > 0) this.syncSubmenuPositions()
    this.recheckHoverBridge()
  }

  /** 断开重连重绑（core onReconnect 钩子）：window resize/scroll 监听 cleanup 摘除后恢复
      （类字段引用，重挂不叠）；展开态中的滚动/缩放翻转重算与桥区重判不失效 */
  protected override onReconnect(): void {
    window.addEventListener('resize', this.repositionOnViewportChange)
    window.addEventListener('scroll', this.repositionOnViewportChange, true)
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <div class="menu-search" part="search" hidden>
        <input class="menu-search-input" part="search-input" type="search" autocomplete="off" />
      </div>
      <ul class="menu" part="menu" role="menu" tabindex="0"></ul>
      <div class="menu-search-empty" part="search-empty" hidden></div>
    `
  }

  /** 缓存节点引用 + 绑定事件 + 注册清理（render 与水合路径共用） */
  private bind(): void {
    const menuEl = this.shadow.querySelector<HTMLElement>('.menu')!
    this.menuEl = menuEl
    menuEl.addEventListener('keydown', (e) => this.handleKey(e as KeyboardEvent))
    // searchable：顶部过滤输入框（input 实时过滤，Escape 清空，ArrowDown 进入菜单）
    const searchInput = this.shadow.querySelector<HTMLInputElement>('.menu-search-input')
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        this.searchTerm = searchInput.value
        this.activeStack = []
        this.activeIndex = -1
        this.renderItems()
      })
      searchInput.addEventListener('keydown', (e) => this.handleSearchKey(e as KeyboardEvent))
    }
    // 鼠标移出整个菜单时收起所有浮层（仅浮出形态：vertical/horizontal 的 popup 子菜单是
    // 瞬态浮层，移出收起是通行惯例；inline 侧边导航的展开态是持续导航上下文——
    // 可能还受控于宿主 expanded 属性，鼠标移出菜单区域不得收起）
    menuEl.addEventListener('mouseleave', (e) => {
      if (this.getAttr('mode') === 'inline' || this.expanded.size === 0) return
      // 水平模式下回折/夹取后的面板与父项横向错开：指针从父项斜向移向面板的路径会短暂落在
      // 菜单盒与面板盒之间的页面区域上（面板越高、错开越多越明显）。此处若直接收起，面板在
      // 指针到达前消失（hover 断链）——落在桥区内时保留展开态，交给桥区观察器判定真正离开。
      const me = e as MouseEvent
      if (this.inHoverBridge(me.clientX, me.clientY)) {
        this.watchHoverBridge(me.clientX, me.clientY)
        return
      }
      this.expanded.clear()
      this.syncOpen()
    })
    // 视口尺寸/滚动变化时重算子菜单翻转（仅展开态下有意义，浮层是瞬时的）
    window.addEventListener('resize', this.repositionOnViewportChange)
    this.onCleanup(() => window.removeEventListener('resize', this.repositionOnViewportChange))
    window.addEventListener('scroll', this.repositionOnViewportChange, true)
    this.onCleanup(() => window.removeEventListener('scroll', this.repositionOnViewportChange, true))
    // 桥区观察器随实例断开清理（挂载/卸载由 watchHoverBridge / stopHoverBridgeWatch 管理）
    this.onCleanup(() => this.stopHoverBridgeWatch())
    // open-on-hover 延迟定时器随实例断开清理
    this.onCleanup(() => {
      this.clearHoverOpenTimer()
      this.clearHoverCloseTimer()
    })
    // 水平溢出收纳：horizontal 模式监听容器宽度变化，重算收纳
    if (typeof ResizeObserver !== 'undefined') {
      this.overflowObserver = new ResizeObserver(() => this.syncOverflowCollapse())
      this.overflowObserver.observe(this)
      this.onCleanup(() => this.overflowObserver?.disconnect())
    }
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（menu 列表存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('.menu')) return false
    this.bind()
    return true
  }

  /**
   * `items` 属性每次变更（含移除）即让 property 通道退位——兑现「最后写入通道胜出」契约：
   * property setter 只写 propertyItems 不碰属性，若不在此复位，宿主在 `.items = [...]` 之后再
   * 设置/移除 `items` 属性会被 property 永久遮蔽（attribute 变更永远不会触发 parseItems）。
   * 消费方 oas-dropdown/oas-context-menu 切回属性通道时本就先 `items = null`，此处为直用 oas-menu
   * 的宿主兜底（与 api 描述/PRD 承诺一致）。
   */
  override attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'items' && oldValue !== newValue) this.propertyItems = null
    super.attributeChangedCallback(name, oldValue, newValue)
  }

  protected override update(): void {
    // RTL 镜像开关：子菜单展开侧/翻转判定与 CSS 镜像（chevron 等）消费
    this.toggleAttribute('data-rtl', isRtl(this))
    // 整单禁用：宿主 aria-disabled 同步（视觉降饱和走 CSS :host([disabled])，同 dropdown 惯例）
    this.setAttribute('aria-disabled', String(this.hasAttr('disabled')))
    // 子元素通道观察器（重连后重建；items 属性显式时子元素被忽略，观察器空转无副作用）
    this.ensureChildObserver()
    // 三通道优先级：items property（对象数组，可携带 leading 节点）> items 属性（JSON）> 子元素收敛
    if (this.propertyItems != null) {
      /* itemsList 已由 property setter 接管，不重解析 */
    } else if (this.hasAttribute('items')) this.parseItems()
    else this.parseChildItems()
    this.syncTheme()
    this.syncMaxHeight()
    this.syncSearch()
    this.syncInlineMode()
    this.syncExpandedAttr()
    this.renderItems()
    // 渲染后检测水平溢出收纳（horizontal 模式）；requestAnimationFrame 等布局完成
    // （update 在 render 后但 flex 布局可能未完成，clientWidth 此时不可靠）
    if (this.getAttr('mode') === 'horizontal') {
      requestAnimationFrame(() => this.syncOverflowCollapse())
    }
  }

  /** inline 模式类标记 + accordion 标记 */
  private syncInlineMode(): void {
    this.classList.toggle('oas-menu--inline', this.getAttr('mode') === 'inline')
    this.classList.toggle('oas-menu--accordion', this.hasAttr('accordion'))
  }

  /** expanded 受控：expanded 属性（JSON 数组）存在时驱动内部 expanded Set */
  private syncExpandedAttr(): void {
    const raw = this.getAttr('expanded', '')
    if (!raw) return // 非受控：内部管理
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        this.expanded = new Set(parsed.filter((v): v is string => typeof v === 'string'))
      }
    } catch {
      // 非法 JSON 忽略
    }
  }

  /** max-height 属性 → CSS 变量（数字补 px，其余原样） */
  private syncMaxHeight(): void {
    const raw = this.getAttr('max-height', '')
    if (raw) {
      const v = /^\d+$/.test(raw) ? `${raw}px` : raw
      this.style.setProperty('--oas-menu-max-height', v)
    } else {
      this.style.removeProperty('--oas-menu-max-height')
    }
  }

  // ===== searchable：顶部过滤输入框 =====

  /** searchable 属性 → 输入框显隐与 i18n 文案；关闭时清空残留过滤态 */
  private syncSearch(): void {
    const wrap = this.shadow.querySelector<HTMLElement>('.menu-search')
    if (!wrap) return
    const on = this.hasAttr('searchable')
    wrap.hidden = !on
    const input = wrap.querySelector<HTMLInputElement>('.menu-search-input')
    if (input) {
      const label = this.t('select.search')
      input.setAttribute('placeholder', label)
      input.setAttribute('aria-label', label)
      if (input.value !== this.searchTerm) input.value = this.searchTerm
    }
    if (!on && this.searchTerm !== '') {
      this.searchTerm = ''
    }
  }

  /** 当前是否处于过滤态（searchable 开启且有非空关键词） */
  private searchActive(): boolean {
    return this.hasAttr('searchable') && this.searchTerm.trim() !== ''
  }

  /**
   * 过滤视图：递归保留 label 命中项或有命中后代的项（祖先保留 + 命中子树整体可见）；
   * group 有命中子项才保留；分隔线去首尾/相邻重复。同时记录命中祖先到 searchExpand
   * （过滤态自动展开命中路径，不改动受控 expanded）。
   */
  private viewItems(): MenuItem[] {
    if (!this.searchActive()) {
      this.searchExpand = new Set()
      return this.itemsList
    }
    const term = this.searchTerm.trim().toLowerCase()
    const expand = new Set<string>()
    const walk = (list: MenuItem[]): MenuItem[] => {
      const out: MenuItem[] = []
      for (const item of list) {
        if (item.type === 'divider') {
          out.push(item)
          continue
        }
        if (item.type === 'group') {
          const kids = item.children ? walk(item.children) : []
          if (kids.some((k) => k.type !== 'divider')) out.push({ ...item, children: kids })
          continue
        }
        const selfMatch = (item.label ?? '').toLowerCase().includes(term)
        // 自身命中保留完整子树；否则递归过滤后代
        const kids = item.children ? (selfMatch ? item.children : walk(item.children)) : undefined
        const kidMatch = kids ? kids.some((k) => k.type !== 'divider') : false
        if (selfMatch || kidMatch) {
          if (!selfMatch && kidMatch && item.value != null) expand.add(item.value)
          out.push(item.children ? { ...item, children: kids } : { ...item })
        }
      }
      return trimDividers(out)
    }
    const view = walk(this.itemsList)
    this.searchExpand = expand
    return view
  }

  /** 过滤无匹配空态（复用 i18n select.noMatch） */
  private syncSearchEmpty(items: MenuItem[]): void {
    const emptyEl = this.shadow.querySelector<HTMLElement>('.menu-search-empty')
    if (!emptyEl) return
    const show = this.searchActive() && this.flattenLevel(items).length === 0
    emptyEl.hidden = !show
    if (show) emptyEl.textContent = this.t('select.noMatch')
  }

  /** 过滤输入框键盘：Escape 清空；ArrowDown 把焦点交给菜单并高亮首个可见项 */
  private handleSearchKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault()
      const input = e.target as HTMLInputElement
      input.value = ''
      this.searchTerm = ''
      this.activeStack = []
      this.activeIndex = -1
      this.renderItems()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.menuEl?.focus({ preventScroll: true })
      const list = this.currentItems()
      const idx = list.findIndex((i) => !i.disabled && !i.loading)
      this.activeIndex = idx
      this.syncActive()
    }
  }

  /** items property 通道值（非 null 时 update 不再重解析 attribute/子元素通道——外部对象数组接管渲染数据） */
  private propertyItems: MenuItem[] | null = null

  /**
   * items property 通道（对象数组直赋）：与 attribute JSON 通道同字段，但可携带
   * `leading` 克隆节点（JSON attribute 通道无法序列化 Node）。赋值立即生效；
   * 此后 attribute `items` 重新解析或子元素通道接管时失效（最后写入的通道胜出）。
   * 主要消费方：oas-dropdown 等宿主把解析后的 MenuItem[]（含 leading 节点）直传渲染。
   */
  get items(): MenuItem[] | null {
    return this.propertyItems
  }

  set items(value: MenuItem[] | null) {
    this.propertyItems = value
    if (value != null) {
      this.itemsList = value
      this.pruneState()
    }
    // 两种赋值都立即重渲染：null（退位）时不重渲染会让 UI 停留在 property 旧数据，
    // 违背「最后写入通道胜出」——update 会回落 attribute/子元素通道重新解析
    this.requestUpdate()
  }

  private parseItems(): void {
    // attribute 通道重新解析：property 通道退位（最后写入胜出）
    this.propertyItems = null
    try {
      const parsed = JSON.parse(this.getAttr('items', '[]'))
      this.itemsList = Array.isArray(parsed)
        ? parsed.filter((i: MenuItem): i is MenuItem => {
            if (!i || typeof i !== 'object') return false
            if (i.type === 'divider') return true
            if (i.type === 'group') return Array.isArray(i.children)
            return typeof i.value === 'string'
          })
        : []
    } catch {
      this.itemsList = []
    }
    this.pruneState()
  }

  // ===== 子元素声明式通道 =====

  /**
   * 子元素通道解析层：把 light DOM 的 `<oas-menu-item>` / `<oas-menu-group>` /
   * `<oas-menu-divider>` 收敛为内部 items 模型（与 parseItems 单一渲染路径，
   * 后续 pruneState/render 全部无感）。
   */
  private parseChildItems(): void {
    this.itemsList = this.parseChildLevel(this.children)
    this.pruneState()
  }

  /** 解析一层子元素为 MenuItem[]（仅识别数据载体元素，其余 light DOM 内容忽略） */
  private parseChildLevel(elements: HTMLCollection | Element[]): MenuItem[] {
    const items: MenuItem[] = []
    for (const child of Array.from(elements)) {
      if (child.tagName === 'OAS-MENU-ITEM') {
        items.push(this.childToMenuItem(child))
      } else if (child.tagName === 'OAS-MENU-GROUP') {
        items.push(this.childToGroup(child))
      } else if (child.tagName === 'OAS-MENU-DIVIDER') {
        items.push({ type: 'divider' })
      }
    }
    return items
  }

  /** 单个 <oas-menu-item> → MenuItem（默认插槽文本为 label，属性对齐 items 字段；嵌套子元素递归为 children） */
  private childToMenuItem(el: Element): MenuItem {
    const item: MenuItem = { label: this.childLabel(el) }
    const value = el.getAttribute('value')
    if (value) item.value = value
    if (el.hasAttribute('disabled')) item.disabled = true
    if (el.hasAttribute('loading')) item.loading = true
    const icon = el.getAttribute('icon')
    if (icon) item.icon = icon
    const iconColor = el.getAttribute('icon-color')
    if (iconColor) item.iconColor = iconColor
    const kind = el.getAttribute('kind')
    if (kind) item.kind = kind as MenuItemKind
    if (el.hasAttribute('danger')) item.danger = true
    const href = el.getAttribute('href')
    if (href) item.href = href
    const target = el.getAttribute('target')
    if (target) item.target = target
    const rel = el.getAttribute('rel')
    if (rel) item.rel = rel
    const shortcut = el.getAttribute('shortcut')
    if (shortcut) item.shortcut = shortcut
    const leading = OASMenuItem.extractLeadingFrom(el)
    if (leading.length > 0) item.leading = leading
    const children = this.parseChildLevel(el.children)
    if (children.length > 0) item.children = children
    return item
  }

  /** 单个 <oas-menu-group> → 分组项（label/value 属性 + 子元素平铺 children） */
  private childToGroup(el: Element): MenuItem {
    const item: MenuItem = { type: 'group', label: el.getAttribute('label') ?? '' }
    const value = el.getAttribute('value')
    if (value) item.value = value
    const children = this.parseChildLevel(el.children)
    if (children.length > 0) item.children = children
    return item
  }

  /** 默认插槽 label 文本：跳过嵌套数据载体元素（其文本属于子菜单而非 label）
   *  与前置媒体节点（判定工具挂数据载体类：template[slot="leading"] 的 content 文本、
   *  [slot="leading"] 元素文本不进 label） */
  private childLabel(el: Element): string {
    let text = ''
    for (const node of el.childNodes) {
      if (node instanceof Element) {
        const tag = node.tagName
        if (tag === 'OAS-MENU-ITEM' || tag === 'OAS-MENU-GROUP' || tag === 'OAS-MENU-DIVIDER') {
          continue
        }
        if (OASMenuItem.isLeadingNode(node)) continue
      }
      text += node.textContent ?? ''
    }
    return text.trim()
  }

  /**
   * 子元素通道观察器：只监听 light DOM 子元素（数据载体增删/属性/文本变化 → 重解析）。
   * 组件自身动作不写 light DOM，无需自引用守卫。
   */
  private ensureChildObserver(): void {
    if (this.childObserver) return
    const observer = new MutationObserver(() => {
      this.update()
    })
    observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: [
        'value',
        'disabled',
        'loading',
        'icon',
        'icon-color',
        'kind',
        'danger',
        'href',
        'target',
        'rel',
        'shortcut',
        'label',
        'slot',
      ],
    })
    this.childObserver = observer
    this.onCleanup(() => {
      observer.disconnect()
      this.childObserver = null
    })
  }

  /** theme="dark" 时局部注入暗色 token（data-theme 到自身，子树继承），独立于全局主题 */
  private syncTheme(): void {
    if (this.getAttr('theme') === 'dark') {
      this.dataset.theme = 'dark'
    } else {
      delete this.dataset.theme
    }
  }

  /** 数据变化后清理失效的展开/导航状态 */
  private pruneState(): void {
    const valid = new Set<string>()
    const collect = (items: MenuItem[]): void => {
      for (const i of items) {
        if (i.value != null) valid.add(i.value)
        if (i.children) collect(i.children)
      }
    }
    collect(this.itemsList)
    for (const v of [...this.expanded]) {
      if (!valid.has(v)) this.expanded.delete(v)
    }
    this.activeStack = this.activeStack.filter((v) => valid.has(v))
    const items = this.currentItems()
    if (this.activeIndex >= items.length) this.activeIndex = items.length > 0 ? 0 : -1
  }

  /** 当前键盘导航层级的可导航项：group 内联展开、divider/组标题跳过（过滤态用过滤视图） */
  private currentItems(): MenuItem[] {
    let items = this.viewItems()
    for (const v of this.activeStack) {
      const parent = items.find((i) => i.value === v)
      if (!parent || !parent.children) return []
      items = parent.children
    }
    return this.flattenLevel(items)
  }

  /** 把一层菜单拍平成可导航项序列（group 子项就地展开，divider/组标题剔除） */
  private flattenLevel(items: MenuItem[]): MenuItem[] {
    const flat: MenuItem[] = []
    const walk = (list: MenuItem[]): void => {
      for (const i of list) {
        if (i.type === 'divider') continue
        if (i.type === 'group') {
          if (i.children) walk(i.children)
          continue
        }
        flat.push(i)
      }
    }
    walk(items)
    return flat
  }

  /** 从根到 value 的祖先链（含 value 自身）；无 value 的 group/divider 不进入链 */
  private chainOf(value: string): string[] {
    const chain: string[] = []
    const walk = (items: MenuItem[], trail: string[]): boolean => {
      for (const item of items) {
        if (item.value === value) {
          chain.push(...trail, value)
          return true
        }
        if (item.children) {
          const nextTrail = item.value != null ? [...trail, item.value] : trail
          if (walk(item.children, nextTrail)) return true
        }
      }
      return false
    }
    walk(this.itemsList, [])
    return chain
  }

  /** 全量渲染一次（含所有子菜单），显隐/激活由 class 控制，不随 hover 重建 */
  private renderItems(): void {
    const menuEl = this.menuEl
    if (!menuEl) return
    menuEl.innerHTML = ''
    const items = this.viewItems()
    this.renderLevel(menuEl, items, '', 0)
    this.syncOpen()
    this.syncActive()
    this.syncSearchEmpty(items)
  }

  /**
   * 递归渲染一层菜单。scope = 叶子归属的 radio 组 id（最近 `type:"group"` 祖先的 value，
   * 无组为 ''）；group 递归时把组 id 传下去。
   */
  private renderLevel(container: HTMLElement, items: MenuItem[], scope: string, depth: number): void {
    const horizontal = this.getAttr('mode') === 'horizontal'
    const inline = this.getAttr('mode') === 'inline'
    for (const item of items) {
      if (item.type === 'divider') {
        const li = document.createElement('li')
        li.className = 'divider'
        li.setAttribute('part', 'divider')
        li.setAttribute('role', 'separator')
        li.setAttribute('aria-hidden', 'true')
        container.appendChild(li)
        continue
      }
      if (item.type === 'group') {
        const li = document.createElement('li')
        li.className = 'group'
        li.setAttribute('part', 'group')
        li.setAttribute('role', 'none')
        const label = document.createElement('span')
        label.className = 'group-label'
        label.textContent = item.label ?? ''
        li.appendChild(label)
        container.appendChild(li)
        // 组 id = 该 group 的 value；未声明 value 的 group 沿用外层作用域
        const nextScope = item.value != null ? item.value : scope
        // 组内子项平铺在同一个列表层级
        if (item.children) this.renderLevel(container, item.children, nextScope, depth)
        continue
      }
      // href 链接项：渲染 <a>（锚点语义：右键新窗口/中键/SEO）；否则 <li>
      const li = document.createElement(item.href ? 'a' : 'li') as HTMLElement
      li.className = 'item'
      li.setAttribute('part', 'item')
      if (item.href) {
        li.setAttribute('href', item.href)
        if (item.target) li.setAttribute('target', item.target)
        if (item.rel) li.setAttribute('rel', item.rel)
      }
      if (item.value != null) li.dataset.value = item.value
      const loading = item.loading ?? false
      const inert = item.disabled || loading
      li.setAttribute('aria-disabled', String(inert))
      // loading 态：aria-busy 同步 + .loading 类（spinner 视觉 + 禁点光标）
      if (loading) {
        li.classList.add('loading')
        li.setAttribute('aria-busy', 'true')
      }
      // 收起态（collapsed）下 label 隐藏，需以 aria-label 兜底可访问名称
      if (item.label) li.setAttribute('aria-label', item.label)
      const hasChildren = !!item.children && item.children.length > 0
      const action = !hasChildren && this.isActionItem(item)
      // 组作用域标记：radio 叶子带所在组 id（无组为 ''）
      if (!hasChildren && !action) li.dataset.scope = scope
      if (loading) {
        // loading 项：spinner 替换图标位（若后续恢复，items 重建即还原 icon）
        const spin = document.createElement('span')
        spin.className = 'spin'
        spin.setAttribute('aria-hidden', 'true')
        li.appendChild(spin)
      } else if (item.leading && item.leading.length > 0) {
        // 前置媒体优先于 icon（头像/色块等富内容替位图标）
        const media = document.createElement('span')
        media.className = 'media'
        media.setAttribute('part', 'media')
        for (const node of item.leading) media.appendChild(node)
        li.appendChild(media)
      } else if (item.icon) {
        const ic = this.createIcon(item.icon, 'icon', item.iconColor)
        if (ic) li.appendChild(ic)
      }
      const label = document.createElement('span')
      label.className = 'label'
      label.textContent = item.label ?? ''
      if (hasChildren) {
        li.setAttribute('role', 'menuitem')
        li.setAttribute('aria-haspopup', 'menu')
        li.setAttribute('aria-expanded', 'false')
        // 箭头用 SVG chevron（光学居中，避免文本字形偏下）
        const arrowName = horizontal && depth === 0 ? 'chevron-down' : 'chevron-right'
        const arrow = this.createIcon(arrowName, 'arrow')
        li.appendChild(label)
        if (item.shortcut) li.appendChild(this.createShortcut(item.shortcut))
        if (arrow) li.appendChild(arrow)
        li.addEventListener('click', (e: MouseEvent) => {
          e.stopPropagation()
          if (this.isDisabled()) {
            e.preventDefault()
            return
          }
          if (item.disabled || item.loading) return
          this.toggleExpand(item.value ?? '')
        })
        // 父项 hover：open-on-hover 时延迟开/移出延迟关；否则即时 hoverExpand（现状）
        li.addEventListener('mouseenter', () => {
          if (this.isDisabled() || item.disabled || item.loading) return
          this.onParentHoverEnter(item.value ?? '')
        })
        li.addEventListener('mouseleave', () => {
          if (this.isDisabled() || item.disabled || item.loading) return
          this.onParentHoverLeave(item.value ?? '')
        })
        // 子菜单：inline 模式就地展开（inline-sub 容器，在父项 li 之后缩进展开）；
        // 非 inline 浮出（ul.submenu，显隐由 .open class 控制，hover 不重建 DOM）
        if (inline) {
          const subWrap = document.createElement('div')
          subWrap.className = 'inline-sub'
          subWrap.setAttribute('data-parent', item.value ?? '')
          // inline-sub 是结构包裹层（无 role）；role=menu 在内部 ul 上（aria-required-children：
          // role=menu 需 menuitem 子元素，包裹层 div 不应冒充 menu）
          const subUl = document.createElement('ul')
          subUl.setAttribute('role', 'menu')
          this.renderLevel(subUl, item.children!, '', depth + 1)
          subWrap.appendChild(subUl)
          li.appendChild(subWrap)
        } else {
          const sub = document.createElement('ul')
          sub.className = depth === 0 ? 'submenu submenu-1' : 'submenu'
          sub.setAttribute('part', 'submenu')
          sub.setAttribute('role', 'menu')
          this.renderLevel(sub, item.children!, '', depth + 1)
          li.appendChild(sub)
        }
      } else {
        const action = this.isActionItem(item)
        const checkbox = !action && item.kind === 'checkbox'
        // role：action=menuitem（无勾选态）/ checkbox=menuitemcheckbox / radio（默认）=menuitemradio
        li.setAttribute('role', action ? 'menuitem' : checkbox ? 'menuitemcheckbox' : 'menuitemradio')
        // danger 破坏性项：红色语义
        if (item.danger) li.classList.add('danger')
        if (!action) {
          li.setAttribute(
            'aria-checked',
            String(checkbox ? this.isChecked(item.value) : item.value === this.selectedValueOf(scope)),
          )
          const check = document.createElement('span')
          check.className = checkbox ? 'check check--box' : 'check'
          check.textContent = checkbox ? '' : '✓'
          li.appendChild(check)
        }
        li.append(label)
        if (item.shortcut) li.appendChild(this.createShortcut(item.shortcut))
        li.addEventListener('click', (e: MouseEvent) => {
          e.stopPropagation()
          // 整单禁用：拦截 + preventDefault（href 链接项阻断原生跳转）
          if (this.isDisabled()) {
            e.preventDefault()
            return
          }
          if (item.disabled || item.loading) return
          this.select(item, scope)
        })
        li.addEventListener('mouseenter', () => {
          if (this.isDisabled() || item.disabled || item.loading) return
          this.hoverExpand(item.value ?? '')
        })
      }
      container.appendChild(li)
    }
    // 水平溢出收纳：horizontal 顶层末尾渲染「···」收纳项（menu-more），子菜单是被收项镜像
    if (horizontal && depth === 0) {
      const moreLi = document.createElement('li')
      moreLi.className = 'item menu-more'
      moreLi.setAttribute('part', 'item')
      moreLi.setAttribute('role', 'menuitem')
      moreLi.setAttribute('aria-haspopup', 'menu')
      moreLi.setAttribute('aria-expanded', 'false')
      moreLi.dataset.value = '__more__' // 收纳项固定 value（syncOpen 按此同步展开态）
      moreLi.setAttribute('aria-label', this.t('menu.more'))
      moreLi.hidden = true // 默认隐藏，溢出时 syncOverflowCollapse 显示
      const moreLabel = document.createElement('span')
      moreLabel.className = 'label'
      moreLabel.textContent = '···'
      moreLi.appendChild(moreLabel)
      const moreArrow = this.createIcon('chevron-down', 'arrow')
      if (moreArrow) moreLi.appendChild(moreArrow)
      const moreSub = document.createElement('ul')
      moreSub.className = 'submenu submenu-1 menu-more-sub'
      moreSub.setAttribute('role', 'menu')
      moreLi.appendChild(moreSub)
      moreLi.addEventListener('click', (e: MouseEvent) => {
        e.stopPropagation()
        if (this.isDisabled()) return
        this.toggleExpand('__more__')
      })
      moreLi.addEventListener('mouseenter', () => {
        if (this.isDisabled()) return
        this.hoverExpand('__more__')
      })
      container.appendChild(moreLi)
      this.moreItemEl = moreLi
    }
  }

  /** 图标名（查表：registerIcon 自定义优先，其次内置注册表）→ 内联 SVG。
   *  iconColor 显式时固定该色（优先于选中/禁用态默认色）；缺省 currentColor 随态着色。
   *  内置单色 path 自带 stroke="currentColor"——iconColor 显式时把 path 的 currentColor 也替换，
   *  否则 path 元素级属性压过 svg 外层 stroke（与 sidebar iconSvg 对齐） */
  private createIcon(icon: string, className = 'icon', iconColor?: string): HTMLElement | null {
    const content = lookupIcon(icon)
    if (!content) return null
    const stroke = iconColor || 'currentColor'
    const coloredContent = iconColor ? content.replace(/stroke="currentColor"/g, `stroke="${stroke}"`) : content
    const span = document.createElement('span')
    span.className = className
    span.setAttribute('aria-hidden', 'true')
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 16 16')
    svg.setAttribute('width', '1em')
    svg.setAttribute('height', '1em')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('stroke', stroke)
    svg.setAttribute('stroke-width', '1.5')
    svg.setAttribute('stroke-linecap', 'round')
    svg.setAttribute('stroke-linejoin', 'round')
    svg.innerHTML = coloredContent
    span.appendChild(svg)
    return span
  }

  /** 快捷键 kbd 标注（右端；与 menubar shortcut 同视觉契约） */
  private createShortcut(text: string): HTMLElement {
    const kbd = document.createElement('kbd')
    kbd.className = 'shortcut'
    kbd.setAttribute('part', 'shortcut')
    kbd.textContent = text
    return kbd
  }

  /** 展开状态 → .open class（不重建 DOM） */
  private syncOpen(): void {
    // 全部收起 → 桥区观察器随之卸载（无常驻监听）
    if (this.expanded.size === 0) this.stopHoverBridgeWatch()
    if (!this.menuEl) return
    const searchOpen = this.searchActive() ? this.searchExpand : null
    for (const li of this.menuEl.querySelectorAll<HTMLElement>('[part="item"][data-value]')) {
      const v = li.dataset.value ?? ''
      const open = this.expanded.has(v) || (searchOpen?.has(v) ?? false)
      li.classList.toggle('open', open)
      if (open) li.setAttribute('aria-expanded', 'true')
      else if (li.hasAttribute('aria-haspopup')) li.setAttribute('aria-expanded', 'false')
    }
    // inline 模式：inline-sub 就地展开容器显隐（expanded 含父 value 时展开）
    for (const sub of this.menuEl.querySelectorAll<HTMLElement>('.inline-sub')) {
      const v = sub.dataset.parent ?? ''
      const open = this.expanded.has(v) || (searchOpen?.has(v) ?? false)
      sub.classList.toggle('open', open)
    }
    this.syncSubmenuPositions()
  }

  /**
   * 子菜单回折/上弹边界翻转：行内轴前方空间不足时翻到另一侧（flip-left）、
   * 底部空间不足时向上展开（flip-up）。翻转由样式表类表达，本方法只做测量与切类。
   *
   * 边界按「组件是否裁切」区分：水平模式 `.menu` 有 `overflow-x: clip`（浮层越出盒缘的那段被
   * 真实裁掉，rect/display 正常但视觉不可见）→ 行内轴边界取「视口 ∩ .menu 盒」；
   * 竖向 / inline 无横轴裁剪（溢出可见，面板允许越出自身盒）→ 行内轴边界只取视口。
   * 垂直向一律按视口判定（两种模式都无纵轴裁剪）。
   * 零宽守卫：SSR/未布局环境 .menu rect 全 0，按盒缘判定会误回折 → 回退视口。
   * 多级嵌套逐级检测：querySelectorAll 按 DOM 序遍历（外层先于内层），外层翻转/夹取先生效，
   * 内层 rect 反映外层处理后的真实布局，因此第三级及以上同样逐级判定。
   *
   * 回折后仍越界（面板宽于容器给任一侧的空间：不回折越盒右缘、回折又越盒左缘）时，
   * 追加一步 inline 位移把它夹回裁切边界内（见 clampSubmenuInline）。夹取对**全部层级**生效：
   * 每一级在自身 rect 上独立判定/夹取，边界同为 `.menu` 盒 ∩ 视口；父级先于子级处理，
   * 子级 rect 已含父级夹取位移，故子级基于父级夹后位置再夹（DOM 序天然保证父先子后）。
   */
  private syncSubmenuPositions(): void {
    if (!this.menuEl) return
    const margin = 8
    const vw = window.innerWidth
    const vh = window.innerHeight
    const rtl = isRtl(this)
    const horizontal = this.getAttr('mode') === 'horizontal'
    const menuRect = horizontal ? this.menuEl.getBoundingClientRect() : null
    const inBox = menuRect != null && menuRect.width > 0
    const rightBound = Math.min(vw, inBox ? menuRect!.right : vw) - margin
    const leftBound = Math.max(0, inBox ? menuRect!.left : 0) + margin
    // 真实裁切边界（无安全边距）：.menu 的 overflow-x: clip 裁到其 padding box（= 盒 rect）
    const clipLeft = inBox ? Math.max(0, menuRect!.left) : 0
    const clipRight = inBox ? Math.min(vw, menuRect!.right) : vw
    for (const item of this.menuEl.querySelectorAll<HTMLElement>('.item.open')) {
      const sub = item.querySelector<HTMLElement>(':scope > .submenu')
      if (!sub) continue
      // 先复位回折类与夹取位移再测量：未回折形态的缘才是「前方空间不足」的正确判据，
      // 残留的位移同样会自我印证（已回折/已夹回的 rect 让条件消失时回不去）。
      // 只复位当前这一级：祖先级的位移必须保留，子级 rect 才能反映父级夹后的真实位置。
      sub.classList.remove('flip-left')
      sub.style.removeProperty('transform')
      const subRect = sub.getBoundingClientRect()
      // 行内轴前方空间不足 → 翻到另一侧（LTR 检右缘溢出、RTL 检左缘溢出）
      sub.classList.toggle('flip-left', rtl ? subRect.left < leftBound : subRect.right > rightBound)
      // 重新测量（水平翻转已生效），垂直向同样按实际布局判定
      const subRectV = sub.getBoundingClientRect()
      sub.classList.toggle('flip-up', subRectV.bottom > vh - margin)
      // 回折后仍越出真实裁切边界 → 最小平移夹回。全部层级独立生效（每级用自身 rect）；
      // 「···」收纳弹层除外——它固定右对齐其收纳项，不参与回折/夹取。
      // 竖向/inline 的 inBox 为 false（无横轴裁剪），自然跳过。
      if (inBox && !sub.classList.contains('menu-more-sub')) {
        this.clampSubmenuInline(sub, subRectV, clipLeft, clipRight, rtl)
      }
    }
  }

  /**
   * 把水平模式子菜单（任一层级）沿行内轴夹回真实裁切边界内（overflow-x: clip 的裁切矩形）。
   *
   * 触发场景：面板宽于容器给它的任一侧空间——不回折越盒终点侧、回折又越盒起点侧，
   * 回折后沿裁切边仍留一段残影。做法是写 inline `transform: translateX()`（物理轴，
   * 由实测 rect 推导，LTR/RTL 自动镜像；不新增/改写 CSS 结构，复位即回到声明定位）。
   *
   * 位移取「最小平移」：面板能放进边界时只补足越界的那一侧（此时至多一侧越界）；
   * 面板本身宽于边界时贴书写起点侧（LTR 左缘 / RTL 右缘）对齐，保证起点侧完整可见
   * ——与浮层引擎「宁可起点侧全可见」的夹取语义一致。舍入向外取整，不残留亚像素越界。
   */
  private clampSubmenuInline(sub: HTMLElement, rect: DOMRect, clipLeft: number, clipRight: number, rtl: boolean): void {
    const span = clipRight - clipLeft
    if (span <= 0) return
    const overStart = clipLeft - rect.left // >0 → 越出起点侧（物理左缘）
    const overEnd = rect.right - clipRight // >0 → 越出终点侧（物理右缘）
    if (overStart <= 0 && overEnd <= 0) return
    // 面板宽于边界 → 贴书写起点侧；否则补足越界的一侧（最小位移）
    const dx =
      rect.width > span ? (rtl ? clipRight - rect.right : clipLeft - rect.left) : overStart > 0 ? overStart : -overEnd
    const px = dx > 0 ? Math.ceil(dx) : Math.floor(dx)
    if (px === 0) return
    sub.style.transform = `translateX(${px}px)`
  }

  /**
   * 指针是否仍落在「父项 → 其面板」的 hover 桥区内（仅水平模式）。
   *
   * 桥区 = 菜单盒 ∪ 每个打开项的盒 ∪ 其面板盒 ∪ 两者之间的下方连接矩形
   * （[min(父项左, 面板左), max(父项右, 面板右)] × [菜单下缘, 面板下缘]）。
   *
   * 为什么需要：回折 / 夹取后的面板与父项在行内轴错开（面板右缘落在父项左缘或更靠书写起点
   * 侧），两者盒之间在菜单下缘以下留有一段页面区域。指针从父项斜向移向面板的直线路径会经过
   * 它——这块区域不属于任何组件盒，但语义上仍是「父项 → 面板」的移动路径；只看菜单盒的
   * mouseleave 会在此处误判为「移出菜单」而收起面板，面板在指针到达前消失（面板越高、
   * 错开越多越明显）。据此保留展开态，再由 watchHoverBridge 判定真正离开。
   *
   * 竖排 / inline 不做判定：竖排面板与父项同轴（相邻贴边、无错开），inline 是就地展开
   * （子项在父项流内），都不存在这块缺口。未布局（rect 全 0，SSR / 测试替身）同样不判定，
   * 保持既有「移出菜单即收起」语义（与 syncSubmenuPositions 的零宽守卫同源）。
   */
  private inHoverBridge(x: number, y: number): boolean {
    if (this.getAttr('mode') !== 'horizontal' || !this.menuEl) return false
    const menuRect = this.menuEl.getBoundingClientRect()
    if (menuRect.width <= 0) return false
    const pad = 2 // 亚像素 / 命中测试容差
    const inside = (r: DOMRect): boolean =>
      x >= r.left - pad && x <= r.right + pad && y >= r.top - pad && y <= r.bottom + pad
    if (inside(menuRect)) return true
    for (const item of this.menuEl.querySelectorAll<HTMLElement>('.item.open')) {
      const sub = item.querySelector<HTMLElement>(':scope > .submenu')
      if (!sub) continue
      const subRect = sub.getBoundingClientRect()
      if (subRect.width <= 0) continue
      const itemRect = item.getBoundingClientRect()
      if (inside(itemRect) || inside(subRect)) return true
      // 连接矩形：补齐父项盒与面板盒之间（菜单下缘以下）的缺口
      const left = Math.min(itemRect.left, subRect.left)
      const right = Math.max(itemRect.right, subRect.right)
      const top = Math.min(itemRect.bottom, subRect.top)
      const bottom = Math.max(itemRect.bottom, subRect.bottom)
      if (x >= left - pad && x <= right + pad && y >= top - pad && y <= bottom + pad) return true
    }
    return false
  }

  /**
   * 桥区观察器：指针落在桥区内（已离开菜单盒）时接管收起判定——mousemove 到桥区外即收起。
   * 只在 mouseleave 判定「仍在桥区」时挂载、收起后立即卸载（无常驻全局监听）。
   * 一并监听文档 mouseleave：指针直接移出窗口时不再产生 mousemove，同样收起（避免面板悬挂）。
   * 入参为挂载时的指针位置（mouseleave 事件坐标），供滚动 / 缩放后重判（recheckHoverBridge）。
   */
  private watchHoverBridge(x: number, y: number): void {
    if (this.bridgeWatch) return
    this.bridgePointer = { x, y }
    const move = (e: MouseEvent): void => {
      this.bridgePointer = { x: e.clientX, y: e.clientY }
      if (this.expanded.size === 0) {
        this.stopHoverBridgeWatch()
        return
      }
      if (this.inHoverBridge(e.clientX, e.clientY)) return
      this.stopHoverBridgeWatch()
      this.expanded.clear()
      this.syncOpen()
    }
    const leave = (): void => {
      this.stopHoverBridgeWatch()
      if (this.expanded.size === 0) return
      this.expanded.clear()
      this.syncOpen()
    }
    this.bridgeWatch = { move, leave }
    window.addEventListener('mousemove', move, true)
    document.addEventListener('mouseleave', leave)
  }

  /** 桥区观察器挂载期间：按最后已知指针位置重判（滚动 / 缩放改变几何后调用，见 reposition） */
  private recheckHoverBridge(): void {
    const p = this.bridgePointer
    if (!this.bridgeWatch || !p) return
    if (this.inHoverBridge(p.x, p.y)) return
    this.stopHoverBridgeWatch()
    if (this.expanded.size === 0) return
    this.expanded.clear()
    this.syncOpen()
  }

  /** 卸载桥区观察器（幂等；收起、断开连接时调用） */
  private stopHoverBridgeWatch(): void {
    if (!this.bridgeWatch) return
    window.removeEventListener('mousemove', this.bridgeWatch.move, true)
    document.removeEventListener('mouseleave', this.bridgeWatch.leave)
    this.bridgeWatch = null
    this.bridgePointer = null
  }

  /** 键盘激活态 → .active class（不重建 DOM） */
  private syncActive(): void {
    if (!this.menuEl) return
    for (const li of this.menuEl.querySelectorAll<HTMLElement>('.item.active')) {
      li.classList.remove('active')
    }
    const current = this.currentItems()[this.activeIndex]
    if (!current || current.value == null) return
    const el = this.menuEl.querySelector<HTMLElement>(`[part="item"][data-value="${current.value}"]`)
    el?.classList.add('active')
  }

  /** 选中叶子项后是否收起展开的子菜单。缺省按形态：inline 侧边导航不收（一致）、
      浮出形态收（主流默认）；显式 close-on-select="true"/"false" 覆盖缺省。 */
  private closeOnSelect(): boolean {
    // persistent：选中后不自动收起（close-on-select 族的正向开关，显式优先于 close-on-select）
    if (this.hasAttr('persistent') && this.getAttr('persistent', '') !== 'false') return false
    // 布尔属性语义：存在即 true（含 close-on-select="" 空值），仅显式 "false" 关闭；
    // 未设置（hasAttr 为 false）时按形态缺省——inline 不收、浮出（vertical/horizontal）收
    if (!this.hasAttr('close-on-select')) return this.getAttr('mode') !== 'inline'
    return this.getAttr('close-on-select', '') !== 'false'
  }

  /** 整单禁用：点击/悬停/键盘全拦截（href 链接项同时 preventDefault 阻断原生跳转） */
  private isDisabled(): boolean {
    return this.hasAttr('disabled')
  }

  /** selectable 语义开关：布尔属性族（存在=true 含空值；仅显式 "false" 关闭）。缺省开（现状选中语义） */
  private selectableEnabled(): boolean {
    return this.getAttr('selectable', 'true') !== 'false'
  }

  /** 叶子项是否按动作语义处理：kind=action，或 selectable=false 整单动作化（无勾选态、不写回 value） */
  private isActionItem(item: MenuItem): boolean {
    return item.kind === 'action' || !this.selectableEnabled()
  }

  private select(item: MenuItem, scope = ''): void {
    // 整单禁用：任何叶子激活（点击/键盘/镜像弹层）都拦截
    if (this.isDisabled()) return
    // action 项：动作语义，不参与 value 选中态（不写回、不打勾），只通知宿主
    if (this.isActionItem(item)) {
      this.emit('select', { value: item.value, kind: 'action' })
    } else if (item.kind === 'checkbox') {
      // checkbox 项：多选勾选集，value 为 JSON 数组；点击切换存留
      const checked = this.checkedSet()
      const v = item.value ?? ''
      if (checked.has(v)) checked.delete(v)
      else checked.add(v)
      this.setAttribute('value', JSON.stringify([...checked]))
      this.emit('select', { value: item.value, checked: checked.has(v) })
    } else {
      const next = this.writeValue(scope, item.value ?? '')
      this.setAttribute('value', next)
      this.emit('select', { value: item.value })
    }
    // 选中后子菜单展开态：checkbox 项勾选切换永不收起（连续勾选场景）；
    // 其余按 close-on-select（缺省分形态：inline 不收、浮出收）
    if (item.kind !== 'checkbox' && this.closeOnSelect() && this.expanded.size > 0) {
      this.expanded.clear()
      this.syncOpen()
    }
  }

  /** checkbox 勾选集（value 为 JSON 数组时解析，否则空集） */
  private checkedSet(): Set<string> {
    const raw = this.getAttr('value', '')
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return new Set(parsed.filter((v): v is string => typeof v === 'string'))
    } catch {
      // 非数组：空勾选集
    }
    return new Set()
  }

  /** checkbox 项是否勾选（在勾选集内） */
  private isChecked(value: string | undefined): boolean {
    return value != null && this.checkedSet().has(value)
  }

  /** value 属性 → 组作用域映射。value 为 JSON 对象时按组 id 拆开，否则视为根作用域（''）单值。 */
  private valueMap(): Record<string, string> {
    const raw = this.getAttr('value', '')
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const map: Record<string, string> = {}
        for (const [k, v] of Object.entries(parsed)) {
          if (typeof v === 'string') map[k] = v
        }
        return map
      }
    } catch {
      // 非 JSON：视为纯字符串值，走根作用域
    }
    return { '': raw }
  }

  /** 指定作用域的当前选中值（未选中返回 ''） */
  private selectedValueOf(scope: string): string {
    return this.valueMap()[scope] ?? ''
  }

  /** 写回某个作用域的选中值；若组件 value 当前已是 JSON 对象则原位更新该组，否则整体转成 JSON 对象 */
  private writeValue(scope: string, value: string): string {
    const map = this.valueMap()
    if (scope === '' && Object.keys(map).length === 1 && '' in map) {
      return value // 根作用域单值：保持纯字符串形态
    }
    if (scope !== '' && Object.keys(map).length === 1 && '' in map) {
      // 由纯字符串形态首次写入组作用域：转 JSON 对象（保留原根值或忽略）
      return JSON.stringify({ ...map, [scope]: value })
    }
    const next = { ...map }
    if (scope === '') delete next[''] // 根作用域不参与 JSON 形态
    next[scope] = value
    return JSON.stringify(next)
  }

  /** hover：级联展开到该项所在的单条路径（同级互斥），只切 class 不重建 */
  private hoverExpand(value: string): void {
    if (!value) return
    // inline 模式是 click-to-expand（就地展开），hover 不展开——否则悬停先展开、点击又收起，
    // 鼠标用户永远展不开（hoverExpand 与 toggleExpand 相互抵消）
    if (this.getAttr('mode') === 'inline') return
    const chain = this.chainOf(value)
    if (chain.length === 0) return
    const item = this.findItem(value)
    const open = item?.children?.length ? chain : chain.slice(0, -1)
    const next = new Set(open)
    if (next.size === this.expanded.size && [...next].every((v) => this.expanded.has(v))) return
    this.expanded = next
    this.syncOpen()
  }

  // ---------- open-on-hover：vertical/inline 父项 hover 延迟开合（horizontal / collapsed 不受影响） ----------

  /** open-on-hover 生效判定：属性开启且非 horizontal（维持即时 hover）、非 collapsed（flyout 即时 hover） */
  private hoverDelayMode(): boolean {
    if (!this.hasAttr('open-on-hover')) return false
    if (this.getAttr('mode') === 'horizontal') return false
    if (this.hasAttr('collapsed')) return false
    return true
  }

  private clearHoverOpenTimer(): void {
    if (this.hoverOpenTimer) {
      clearTimeout(this.hoverOpenTimer)
      this.hoverOpenTimer = null
    }
  }

  private clearHoverCloseTimer(): void {
    if (this.hoverCloseTimer) {
      clearTimeout(this.hoverCloseTimer)
      this.hoverCloseTimer = null
    }
    this.hoverCloseValue = null
  }

  /** 父项悬停进入：延迟模式取消挂起关闭（仅同一父项的宽限——异项不清，前项移出满宽限仍收起）+ ~150ms 后展开；现状模式即时 hoverExpand */
  private onParentHoverEnter(value: string): void {
    if (!this.hoverDelayMode()) {
      this.hoverExpand(value)
      return
    }
    if (this.hoverCloseTimer && this.hoverCloseValue === value) this.clearHoverCloseTimer()
    if (this.expanded.has(value)) return
    this.clearHoverOpenTimer()
    this.hoverOpenTimer = setTimeout(() => {
      this.hoverOpenTimer = null
      this.hoverExpandDelayed(value)
    }, 150)
  }

  /** 父项悬停移出：延迟模式 ~300ms 后收起本分支；现状模式无逐项收起（随菜单 mouseleave 收） */
  private onParentHoverLeave(value: string): void {
    if (!this.hoverDelayMode()) return
    this.clearHoverOpenTimer()
    if (!this.expanded.has(value)) return
    this.clearHoverCloseTimer()
    this.hoverCloseValue = value
    this.hoverCloseTimer = setTimeout(() => {
      this.hoverCloseTimer = null
      this.hoverCloseValue = null
      this.collapseBranch(value)
    }, 300)
  }

  /** hover 延迟展开：inline 并入展开集合（其他分支保留，多开语义）；浮出走单链路径（对齐 hoverExpand） */
  private hoverExpandDelayed(value: string): void {
    if (!value) return
    const chain = this.chainOf(value)
    if (chain.length === 0) return
    const item = this.findItem(value)
    const open = item?.children?.length ? chain : chain.slice(0, -1)
    if (this.getAttr('mode') === 'inline') {
      const next = new Set(this.expanded)
      for (const v of open) next.add(v)
      if (this.hasAttr('accordion')) {
        for (const s of this.siblingValuesOf(value)) next.delete(s)
      }
      this.expanded = next
    } else {
      const next = new Set(open)
      if (next.size === this.expanded.size && [...next].every((v) => this.expanded.has(v))) return
      this.expanded = next
    }
    this.syncOpen()
  }

  /** 收起该分支：删除 value 及其全部后代（inline 多开集合与浮出单链语义都适用） */
  private collapseBranch(value: string): void {
    const next = new Set(this.expanded)
    const drop = (v: string): void => {
      next.delete(v)
      const item = this.findItem(v)
      for (const c of item?.children ?? []) drop(c.value ?? '')
    }
    drop(value)
    this.expanded = next
    this.syncOpen()
  }

  /** 点击：展开/收起子菜单（整单禁用时拦截） */
  private toggleExpand(value: string): void {
    if (this.isDisabled()) return
    // 水平收纳项「···」：非数据项，直接切换其展开态（不走 chainOf——不在 items 树里）
    if (value === '__more__') {
      const next = new Set(this.expanded)
      const willExpandMore = !next.has('__more__')
      if (willExpandMore) next.add('__more__')
      else next.delete('__more__')
      this.expanded = next
      this.emit('expand-change', {
        expanded: [...this.expanded],
        value,
        isExpanded: willExpandMore,
      })
      this.syncOpen()
      return
    }
    const inline = this.getAttr('mode') === 'inline'
    const accordion = this.hasAttr('accordion')
    let willExpand: boolean
    if (inline) {
      // inline 多点展开集合：切换该项展开态；accordion 同级互斥（展开一个收起其他同级）
      const next = new Set(this.expanded)
      if (next.has(value)) {
        next.delete(value)
        willExpand = false
      } else {
        if (accordion) {
          // 同级互斥：收起与该 value 同级的其他展开项
          const siblings = this.siblingValuesOf(value)
          for (const s of siblings) next.delete(s)
        }
        next.add(value)
        willExpand = true
      }
      this.expanded = next
    } else {
      // 浮出模式：单条展开路径（现状）
      if (this.expanded.has(value)) {
        this.expanded = new Set(this.chainOf(value).slice(0, -1))
        willExpand = false
      } else {
        this.expanded = new Set(this.chainOf(value))
        willExpand = true
      }
    }
    // oas-expand-change：展开状态变化时派发（受控/非受控都派发；受控时宿主据此更新 expanded 属性）
    this.emit('expand-change', { expanded: [...this.expanded], value, isExpanded: willExpand })
    this.syncOpen()
  }

  /** 该 value 同级（同一父级 children 里）的其他有子菜单项的 value 集合（手风琴互斥用） */
  private siblingValuesOf(value: string): string[] {
    const chain = this.chainOf(value)
    const parentChain = chain.slice(0, -1)
    // 找父级的 children
    let levelItems = this.itemsList
    for (const ancestorValue of parentChain) {
      const found = levelItems.find((i) => i.value === ancestorValue)
      levelItems = found?.children ?? []
    }
    return levelItems.filter((i) => i.children?.length && i.value !== value).map((i) => i.value ?? '')
  }

  /** 水平溢出收纳：horizontal 模式容器宽度不足时，超宽项收进末尾「···」收纳子菜单 */
  private syncOverflowCollapse(): void {
    if (this.getAttr('mode') !== 'horizontal') return
    const menuEl = this.menuEl
    if (!menuEl) return
    // 顶层项（排除收纳项本身——它是镜像容器不是数据项；曾误纳入计算致自己被 data-collapsed 隐藏）
    const topItems = [...menuEl.querySelectorAll<HTMLElement>(':scope > [part="item"][data-value]:not(.menu-more)')]
    if (topItems.length === 0) return
    const moreItem = this.moreItemEl ?? menuEl.querySelector<HTMLElement>('.menu-more')
    // 先复位再测量：collapsed 项 display:none 宽为 0，收纳项隐藏时也不占宽，
    // 直接量会把「已收纳状态」误判成「无溢出」（RO 再次触发时全部弹回）
    topItems.forEach((t) => t.removeAttribute('data-collapsed'))
    if (moreItem) moreItem.hidden = true
    // 显示收纳项量出其宽度（有溢出时它要占位，可用宽度须扣除；无溢出最后会再隐藏）
    let moreWidth = 0
    if (moreItem) {
      moreItem.hidden = false
      moreWidth = moreItem.offsetWidth
      moreItem.hidden = true
    }
    const avail = menuEl.clientWidth
    // 累积宽度，超出可用宽度的标记 data-collapsed
    let acc = 0
    let firstOverflow = -1
    topItems.forEach((t, i) => {
      acc += t.offsetWidth
      if (firstOverflow === -1 && acc > avail) firstOverflow = i
    })
    let hasOverflow = firstOverflow !== -1
    if (hasOverflow && moreWidth > 0) {
      // 有溢出：收纳项自身占 moreWidth，重算首个溢出项（可用宽度 - 收纳项宽）
      const avail2 = avail - moreWidth
      acc = 0
      firstOverflow = -1
      topItems.forEach((t, i) => {
        acc += t.offsetWidth
        if (firstOverflow === -1 && acc > avail2) firstOverflow = i
      })
      if (firstOverflow === -1) firstOverflow = topItems.length - 1 // 兜底：至少收一项腾位
    }
    topItems.forEach((t, i) => {
      t.toggleAttribute('data-collapsed', hasOverflow && i >= firstOverflow)
    })
    // 收纳项「···」显隐 + 子菜单内容（被收项镜像，点击激活对应 value）
    if (!moreItem) return
    moreItem.hidden = !hasOverflow
    const moreSub = moreItem.querySelector<HTMLElement>('.menu-more-sub')
    const selectable = this.selectableEnabled()
    if (moreSub && hasOverflow) {
      moreSub.innerHTML = ''
      const collapsed = topItems.filter((t) => t.hasAttribute('data-collapsed'))
      for (const t of collapsed) {
        const value = t.getAttribute('data-value') ?? ''
        const item = this.findItem(value)
        if (!item) continue
        const li = document.createElement('li')
        li.className = 'item'
        li.setAttribute('part', 'item')
        if (selectable) {
          // 镜像项与主流一致的 radio 语义：role=menuitemradio + aria-checked + 前导 ✓
          // （选中收纳项后弹层内可见选中态，否则用户在弹层里得不到任何反馈）
          li.setAttribute('role', 'menuitemradio')
          li.setAttribute('aria-checked', String(value === this.selectedValueOf('')))
          const check = document.createElement('span')
          check.className = 'check'
          check.textContent = '✓'
          li.appendChild(check)
        } else {
          // selectable=false：镜像项同样降为动作语义（无勾选态）
          li.setAttribute('role', 'menuitem')
        }
        li.setAttribute('data-value', value)
        const label = document.createElement('span')
        label.className = 'label'
        label.textContent = item.label ?? value
        li.appendChild(label)
        li.addEventListener('click', (e: MouseEvent) => {
          e.stopPropagation()
          if (this.isDisabled() || item.disabled || item.loading) return
          this.select(item, '')
        })
        moreSub.appendChild(li)
      }
    }
    // 「···」高亮：当前选中项被收纳时，收纳项显示选中态（选中项在溢出弹层里，
    // 条上看不到 ✓，由收纳指示器本身高亮表达"选中项在其中"）+ aria-current 供读屏
    const selectedInside =
      selectable &&
      hasOverflow &&
      !!this.selectedValueOf('') &&
      topItems.some((t) => t.hasAttribute('data-collapsed') && t.dataset.value === this.selectedValueOf(''))
    moreItem.classList.toggle('child-selected', selectedInside)
    if (selectedInside) moreItem.setAttribute('aria-current', 'true')
    else moreItem.removeAttribute('aria-current')
  }

  private findItem(value: string): MenuItem | undefined {
    let found: MenuItem | undefined
    const walk = (items: MenuItem[]): void => {
      for (const item of items) {
        if (item.value === value) found = item
        else if (item.children) walk(item.children)
      }
    }
    walk(this.itemsList)
    return found
  }

  /** 键盘进入子菜单：展开并高亮第一个可用子项 */
  private enterSubmenu(item: MenuItem): void {
    this.activeStack.push(item.value ?? '')
    this.expanded = new Set(this.activeStack)
    const children = this.currentItems()
    const firstEnabled = children.findIndex((c) => !c.disabled && !c.loading)
    this.activeIndex = firstEnabled >= 0 ? firstEnabled : 0
  }

  /** 键盘返回父级：收起子菜单并高亮父级项 */
  private leaveSubmenu(): void {
    const value = this.activeStack.pop()
    this.expanded = new Set(this.activeStack)
    const parentLevel = this.currentItems()
    this.activeIndex = parentLevel.findIndex((i) => i.value === value)
  }

  private moveActive(enabled: number[], dir: 1 | -1): void {
    const len = enabled.length
    if (this.activeIndex < 0) {
      this.activeIndex = dir === 1 ? enabled[0]! : enabled[len - 1]!
      return
    }
    const cur = enabled.indexOf(this.activeIndex)
    this.activeIndex = enabled[(cur + dir + len) % len]!
  }

  private handleKey(e: KeyboardEvent): void {
    // 整单禁用：键盘导航与激活全拦截
    if (this.isDisabled()) return
    const items = this.currentItems()
    const enabled = items.map((i, idx) => (i.disabled || i.loading ? -1 : idx)).filter((i) => i >= 0)
    if (enabled.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.moveActive(enabled, 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.moveActive(enabled, -1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      const active = items[this.activeIndex]
      if (active && !active.disabled && !active.loading && active.children?.length) {
        this.enterSubmenu(active)
      } else {
        this.moveActive(enabled, 1)
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      if (this.activeStack.length > 0) {
        this.leaveSubmenu()
      } else {
        this.moveActive(enabled, -1)
      }
    } else if (e.key === 'Enter') {
      const active = items[this.activeIndex]
      if (!active) return
      e.preventDefault()
      if (active.disabled || active.loading) return
      if (active.children?.length) {
        this.enterSubmenu(active)
      } else {
        const activeEl = this.menuEl!.querySelector<HTMLElement>(`[part="item"][data-value="${active.value}"]`)
        this.select(active, activeEl?.dataset.scope ?? '')
      }
    } else if (e.key === 'Home') {
      this.activeIndex = enabled[0]!
    } else if (e.key === 'End') {
      this.activeIndex = enabled[enabled.length - 1]!
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // typeahead：可打印字符跳转匹配 label 的项（连续字符缓冲 + 超时重置）
      e.preventDefault()
      this.typeahead(e.key)
    } else {
      return
    }
    this.syncOpen()
    this.syncActive()
  }

  /** typeahead：缓冲字符序列，跳转当前层 label 匹配的项（startsWith 优先，includes 兜底），超时 500ms 重置 */
  private typeahead(char: string): void {
    this.typeaheadBuffer += char.toLowerCase()
    if (this.typeaheadTimer) clearTimeout(this.typeaheadTimer)
    this.typeaheadTimer = setTimeout(() => {
      this.typeaheadBuffer = ''
    }, 500)
    const items = this.currentItems()
    const buf = this.typeaheadBuffer
    // 匹配：label startsWith 优先，无则 includes
    const match = (pred: (s: string) => boolean) =>
      items.findIndex((i) => !i.disabled && !i.loading && i.label && pred(i.label.toLowerCase()))
    let idx = match((s) => s.startsWith(buf))
    if (idx === -1) idx = match((s) => s.includes(buf))
    if (idx === -1) return
    this.activeIndex = idx
    const item = items[idx]
    if (item) {
      const el = this.menuEl!.querySelector<HTMLElement>(`[part="item"][data-value="${item.value}"]`)
      el?.focus({ preventScroll: true })
    }
  }
}
